import { useEffect, useState } from 'react';
import { Box, IconButton, Modal, Typography } from '@mui/material';
import {
  IconWrapper,
  InputField,
  PrimaryButton,
  pxToRem,
  pxTovW,
} from '@geneo2-web/shared-ui';
import { StudentLoginResponseType } from '@protos/user_management/ums.login.apis_pb';
import { useAppDispatch } from '../../../reduxStore/reduxHooks';
import { setToastInfo } from '../reducer/homeDashboard.slice';
import { patchStudentProfile } from '../api/studentProfileApi';
import {
  setIsNameEditPopupShown,
  setUserInfo,
} from '../../Auth/reducer/auth.slice';
import { useLocation } from 'react-router-dom';

type Props = {
  open: boolean;
  onClose: () => void;
  studentProfile?: StudentLoginResponseType;
  onSave?: (firstName: string, lastName: string) => void;
};

const StudentNameEditPopup = ({
  open,
  onClose,
  studentProfile,
  onSave,
}: Props) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (location.pathname !== '/home') {
      setFirstName(studentProfile?.firstName ?? '');
      setLastName(studentProfile?.lastName ?? '');
    }
  }, [studentProfile, open]);

  const handleClose = () => {
    console.log('close');
    dispatch(setIsNameEditPopupShown(true));
    onClose();
  };

  const handleSave = async () => {
    const f = firstName.trim();
    const l = lastName.trim();
    if (!f || !l) {
      dispatch(
        setToastInfo({
          label: 'Please enter first name and last name',
          variant: 'error',
          open: true,
        })
      );
      return;
    }
    setSaving(true);
    try {
      await patchStudentProfile({ firstName: f, lastName: l });
      if (studentProfile) {
        studentProfile.firstName = f;
        studentProfile.lastName = l;
        dispatch(setUserInfo(studentProfile));
      }
      dispatch(
        setToastInfo({
          label: 'Name updated successfully',
          variant: 'success',
          open: true,
        })
      );
      handleClose();
    } catch (err: any) {
      dispatch(
        setToastInfo({
          label: err?.message || 'Failed to update name',
          variant: 'error',
          open: true,
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            backgroundColor: 'white',
            width: { xs: '90%', md: pxTovW(500) },
            borderRadius: { xs: pxToRem(16), md: pxTovW(16) },
            p: { xs: pxToRem(20), md: pxTovW(24) },
            boxShadow: 3,
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: pxToRem(8),
              right: pxToRem(8),
              p: 0,
            }}
          >
            <IconWrapper
              name="close-yellow"
              size="md"
              parentFolder="icons"
              type="png"
              customSx={{
                width: { xs: pxToRem(33), md: pxTovW(54) },
                height: { xs: pxToRem(33), md: pxTovW(54) },
              }}
            />
          </IconButton>
          <Typography
            variant="h2"
            sx={{ mb: { xs: pxToRem(16), md: pxTovW(16) } }}
          >
            Update your name
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                gap: { xs: pxToRem(8), md: pxTovW(10) },
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: {
                    xs: pxToRem(111),
                    md: pxTovW(240),
                    lg: pxTovW(198),
                  },
                  mb: { xs: pxToRem(4), md: 0 },
                }}
              >
                <Typography
                  variant="h3"
                  sx={{ fontSize: { xs: pxToRem(14), md: 'inherit' } }}
                >
                  First Name
                  <Box component="span" sx={{ color: 'red' }}>
                    *
                  </Box>
                  :
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '80%',
                }}
              >
                <InputField
                  type="text"
                  value={firstName}
                  variant="outlined"
                  name="first_name"
                  onChange={(e) => setFirstName(e.target.value)}
                  sx={{
                    width: { xs: '100%' },
                    height: {
                      xs: pxToRem(44),
                      md: pxTovW(44),
                    },
                    borderRadius: {
                      xs: pxToRem(0.8),
                      md: pxToRem(0.8),
                    },
                    '& .MuiOutlinedInput-root': {
                      height: {
                        xs: pxToRem(44),
                        md: pxTovW(44),
                      },
                      border: '1px solid #A8D3FC',
                      padding: '5px',
                      backgroundColor: '#F5FAFF',
                    },
                  }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: { xs: pxToRem(8), md: pxTovW(10) },
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: {
                    xs: pxToRem(111),
                    md: pxTovW(240),
                    lg: pxTovW(198),
                  },
                  mb: { xs: pxToRem(4), md: 0 },
                }}
              >
                <Typography
                  variant="h3"
                  sx={{ fontSize: { xs: pxToRem(14), md: 'inherit' } }}
                >
                  Last Name
                  <Box component="span" sx={{ color: 'red' }}>
                    *
                  </Box>
                  :
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '80%',
                }}
              >
                <InputField
                  type="text"
                  value={lastName}
                  variant="outlined"
                  name="last_name"
                  onChange={(e) => setLastName(e.target.value)}
                  sx={{
                    width: { xs: '100%' },
                    height: {
                      xs: pxToRem(44),
                      md: pxTovW(44),
                    },
                    borderRadius: {
                      xs: pxToRem(0.8),
                      md: pxToRem(0.8),
                    },
                    '& .MuiOutlinedInput-root': {
                      height: {
                        xs: pxToRem(44),
                        md: pxTovW(44),
                      },
                      border: '1px solid #A8D3FC',
                      padding: '5px',
                      backgroundColor: '#F5FAFF',
                    },
                  }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: { xs: pxToRem(12), md: pxTovW(12) },
              }}
            >
              <PrimaryButton onClick={handleSave} disabled={saving}>
                <Typography variant="bodyText" color="white">
                  {saving ? 'Saving…' : 'Save'}
                </Typography>
              </PrimaryButton>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default StudentNameEditPopup;
